import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import Tooltip from "@mui/material/Tooltip";
import { Breadcrumbs } from "components/common/Breadcrumbs";
import { Breadcrumb } from "components/common/Breadcrumbs/types";
import {
  FormDialog,
  selectOptionSchema,
  selectOptionSchemaNotRequired
} from "components/common/FormDialog";
import {
  FIELD_TYPES,
  FormDialogProps,
  SelectOption
} from "components/common/FormDialog/types";
import { Head } from "components/common/Head";
import { Loader } from "components/common/Loader";
import { Table } from "components/common/Table";
import {
  TableColumn,
  TableRowActionsMenuItem
} from "components/common/Table/types";
import { useMount } from "hooks/useMount";
import { usePrevious } from "hooks/usePrevious";
import { useUnmount } from "hooks/useUnmount";
import { enterprisesActions } from "modules/enterprises/slices";
import { organizationSelector } from "modules/enterprises/selectors";
import { networksActions } from "modules/networks/slices";
import {
  isNetworkDeletingSelector,
  isNetworkInterfaceCreatingSelector,
  isNetworkInterfaceDeletingSelector,
  isNetworkInterfaceEditingSelector,
  isNetworkUpdatingSelector,
  isSubnetCreatingSelector,
  isSubnetDeletingSelector,
  isSubnetUpdatingSelector,
  networkSelector,
  networkSubnetsSelector,
  tableFirewallsSelector,
  tableNetworkInterfacesSelector
} from "modules/networks/selectors";
import { Subnet, TableNetworkInterface } from "modules/networks/types";
import { pollingActions } from "modules/polling/slices";
import { createPolling } from "modules/polling/utils";
import { projectsActions } from "modules/projects/slices";
import { projectSelector } from "modules/projects/selectors";
import { FC, useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { generatePath, useNavigate, useParams } from "react-router-dom";
import { generateSearchString } from "utils/generateSearchString";
import { getParentPath } from "utils/getParentPath";
import { getSelectOption } from "utils/getSelectOption";
import { splitMultilineCommaSeparatedList } from "utils/splitMultilineCommaSeparatedList";
import { validateName } from "utils/validateName";
import { array, object, string } from "yup";
import {
  ENTITY_NAME_LENGTH,
  REGEX,
  ROUTES,
  SPECIAL_NAMES
} from "../../constants";
import * as s from "./styles";
import { DIALOG_TYPES, TABS } from "./types";

const POLL_ID_PREFIX = "NETWORK";

const POLL_IDS = {
  network: "NETWORK",
  networkSubnets: "NETWORK_SUBNETS",
  firewalls: "FIREWALLS",
  networkPorts: "NETWORK_PORTS"
};

const subnetTableColumns: TableColumn<Subnet>[] = [
  { key: "name", label: "Name" },
  { key: "id", label: "ID" },
  { key: "cidr", label: "CIDR" },
  { key: "gateway_ip", label: "Gateway IP" }
];

const TAB_TITLES: { [key in TABS]: string } = {
  [TABS.SUBNETS]: "Subnets",
  [TABS.PORTS]: "Ports"
};

export const Network: FC = () => {
  const dispatch = useDispatch();
  const matchParams = useParams<{
    organizationId: string;
    regionId: string;
    projectId: string;
    networkId: string;
  }>();
  const history = useNavigate();
  const organization = useSelector(organizationSelector);
  const project = useSelector(projectSelector);
  const network = useSelector(networkSelector);
  const firewalls = useSelector(tableFirewallsSelector);
  const subnets = useSelector(networkSubnetsSelector);
  const networkInterfaces = useSelector(tableNetworkInterfacesSelector);
  const isNetworkInterfaceEditing = useSelector(
    isNetworkInterfaceEditingSelector
  );
  const isNetworkInterfaceCreating = useSelector(
    isNetworkInterfaceCreatingSelector
  );
  const isNetworkInterfaceDeleting = useSelector(
    isNetworkInterfaceDeletingSelector
  );
  const isNetworkUpdating = useSelector(isNetworkUpdatingSelector);
  const isNetworkDeleting = useSelector(isNetworkDeletingSelector);
  const isSubnetCreating = useSelector(isSubnetCreatingSelector);
  const isSubnetUpdating = useSelector(isSubnetUpdatingSelector);
  const isSubnetDeleting = useSelector(isSubnetDeletingSelector);
  const isNetworkOperationInProgress = isNetworkUpdating || isNetworkDeleting;
  const isSubnetOperationInProgress =
    isSubnetCreating || isSubnetUpdating || isSubnetDeleting;
  const isPortsOperationInProgress =
    isNetworkInterfaceCreating ||
    isNetworkInterfaceEditing ||
    isNetworkInterfaceDeleting;

  // TODO: decide if we want to show port security column only for ports with disabled port security or always
  const [showPortSecurityColumn, setShowPortSecurityColumn] =
    useState<boolean>(false);
  const [showAddressPairsColumn, setShowAddressPairsColumn] =
    useState<boolean>(false);
  useEffect(() => {
    const shouldShow =
      networkInterfaces?.some(
        (port) =>
          (port.device_owner === "" || port.device_owner === "compute:nova") &&
          port.port_security_enabled_string === "disabled"
      ) ?? false;
    setShowPortSecurityColumn(shouldShow);
    const shouldShowPairs =
      networkInterfaces?.some((port) => Boolean(port.addressPairsString)) ??
      false;
    setShowAddressPairsColumn(shouldShowPairs);
  }, [networkInterfaces]);

  const portsTableColumns: TableColumn<TableNetworkInterface>[] = [
    { key: "id", label: "ID", width: "15%" },
    { key: "display_name", label: "Name" },
    // { key: "mac_address", label: "MAC Address" },
    { key: "ips", label: "Fixed IPs" },
    { key: "subnet_names", label: "Subnet" },
    { key: "firewalls_string", label: "Firewalls" },
    ...(showAddressPairsColumn
      ? [
          {
            key: "addressPairsString" as keyof TableNetworkInterface,
            label: "Address Pairs",
            width: "152px"
          }
        ]
      : []),
    { key: "device_owner", label: "Device" },
    { key: "status", label: "Status", width: "9%" },
    { key: "admin_state_string", label: "Admin State", width: "10%" },
    // {
    //   key: "port_security_enabled_string" as keyof TableNetworkInterface,
    //   label: "Port Security",
    //   width: "10%"
    // }
    ...(showPortSecurityColumn
      ? [
          {
            key: "port_security_enabled_string" as keyof TableNetworkInterface,
            label: "Port Security",
            width: "10%"
          }
        ]
      : [])
  ];

  const previousIsNetworkOperationInProgress = usePrevious(
    isNetworkOperationInProgress
  );
  const previousIsSubnetOperationInProgress = usePrevious(
    isSubnetOperationInProgress
  );

  const previousIsPortsOperationInProgress = usePrevious(
    isPortsOperationInProgress
  );
  const [selectedItemId, setSelectedItemId] = useState<string | null>(null);
  const [dialog, setDialog] = useState<{
    isOpened: boolean;
    type: DIALOG_TYPES;
  }>({ type: DIALOG_TYPES.EDIT_NETWORK, isOpened: false });

  const handleCloseDialog = useCallback(() => {
    setDialog({
      ...dialog,
      isOpened: false
    });
    setSelectedItemId(null);
  }, [dialog]);

  const activeTabIndexFromParam = Object.keys(TAB_TITLES).find(
    (key) =>
      TAB_TITLES[key] &&
      String(TAB_TITLES[key]).toLowerCase() ===
        new URLSearchParams(location.search).get("tab")
  );
  const [activeTabIndex, setActiveTabIndex] = useState<TABS>(
    Number(activeTabIndexFromParam || TABS.SUBNETS)
  );
  const handleChangeTab = useCallback((e, value: number) => {
    setActiveTabIndex(value);
  }, []);

  const breadcrumbs: Breadcrumb[] = [
    { text: "Organizations", url: ROUTES.ORGANIZATIONS },
    {
      text: organization?.name || "",
      url: generatePath(ROUTES.ORGANIZATION, {
        organizationId: matchParams.organizationId
      })
    },
    {
      text: "Projects",
      url: generatePath(ROUTES.ORGANIZATION, {
        organizationId: matchParams.organizationId
      })
    },
    {
      text: project?.name || "",
      url: generatePath(ROUTES.PROJECT, {
        organizationId: matchParams.organizationId,
        regionId: matchParams.regionId,
        projectId: matchParams.projectId
      })
    },
    {
      text: "Networking",
      url: generatePath(ROUTES.NETWORKING, {
        organizationId: matchParams.organizationId,
        regionId: matchParams.regionId,
        projectId: matchParams.projectId
      })
    },
    {
      text: network?.name || "",
      url: generatePath(ROUTES.NETWORK, {
        organizationId: matchParams.organizationId,
        regionId: matchParams.regionId,
        projectId: matchParams.projectId,
        networkId: matchParams.networkId
      })
    }
  ];

  const handleEditNetworkButtonClick = useCallback(() => {
    setDialog({
      type: DIALOG_TYPES.EDIT_NETWORK,
      isOpened: true
    });
  }, []);

  const handleDeleteNetworkButtonClick = useCallback(() => {
    setDialog({
      type: DIALOG_TYPES.DELETE_NETWORK,
      isOpened: true
    });
  }, []);

  const handleCreateSubnetButtonClick = useCallback(() => {
    setDialog({
      type: DIALOG_TYPES.CREATE_SUBNET,
      isOpened: true
    });
  }, []);

  const handleEditSubnetMenuItemClick = useCallback((id: string) => {
    setSelectedItemId(id);
    setDialog({
      type: DIALOG_TYPES.EDIT_SUBNET,
      isOpened: true
    });
  }, []);

  const handleDeleteSubnetMenuItemClick = useCallback((id: string) => {
    setSelectedItemId(id);
    setDialog({
      type: DIALOG_TYPES.DELETE_SUBNET,
      isOpened: true
    });
  }, []);

  const handleCreatePortButtonClick = useCallback(() => {
    setDialog({
      type: DIALOG_TYPES.CREATE_PORT,
      isOpened: true
    });
  }, []);

  const handleEditPortMenuItemClick = useCallback((id: string) => {
    setSelectedItemId(id);
    setDialog({
      type: DIALOG_TYPES.EDIT_PORT,
      isOpened: true
    });
  }, []);

  const handleEnablePortMenuItemClick = useCallback((id: string) => {
    setSelectedItemId(id);
    setDialog({
      type: DIALOG_TYPES.ENABLE_PORT,
      isOpened: true
    });
  }, []);

  const handleDisablePortMenuItemClick = useCallback((id: string) => {
    setSelectedItemId(id);
    setDialog({
      type: DIALOG_TYPES.DISABLE_PORT,
      isOpened: true
    });
  }, []);

  const handleDeletePortMenuItemClick = useCallback((id: string) => {
    setSelectedItemId(id);
    setDialog({
      type: DIALOG_TYPES.DELETE_PORT,
      isOpened: true
    });
  }, []);

  const subnetTableActions: TableRowActionsMenuItem<Subnet>[] = [
    {
      label: "Edit",
      handler: handleEditSubnetMenuItemClick
    },
    {
      label: "Delete",
      handler: handleDeleteSubnetMenuItemClick
    }
  ];

  const portTableActions: TableRowActionsMenuItem<TableNetworkInterface>[] = [
    {
      label: "Enable",
      handler: handleEnablePortMenuItemClick,
      isDisabled: (port) => port.admin_state_up
    },
    {
      label: "Edit",
      handler: handleEditPortMenuItemClick,
      isDisabled: (port) =>
        !(port.device_owner === "" || port.device_owner === "compute:nova")
    },
    {
      label: "Disable",
      handler: handleDisablePortMenuItemClick,
      isDisabled: (port) => !port.admin_state_up
    },
    {
      label: "Delete",
      handler: handleDeletePortMenuItemClick
    }
  ];

  useMount(() => {
    dispatch(
      createPolling({
        id: `${POLL_ID_PREFIX}/${POLL_IDS.network}`,
        actionCreator: networksActions.getNetworkRequest,
        params: {
          projectId: matchParams.projectId!,
          regionId: matchParams.regionId!,
          id: matchParams.networkId!
        }
      })
    );
    dispatch(
      enterprisesActions.getOrganizationRequest({
        id: matchParams.organizationId!
      })
    );
    dispatch(
      projectsActions.getProjectRequest({
        regionId: matchParams.regionId!,
        id: matchParams.projectId!
      })
    );
    dispatch(
      createPolling({
        id: `${POLL_ID_PREFIX}/${POLL_IDS.firewalls}`,
        actionCreator: networksActions.getFirewallsRequest,
        params: {
          regionId: matchParams.regionId!,
          projectId: matchParams.projectId!
        }
      })
    );
    dispatch(
      createPolling({
        id: `${POLL_ID_PREFIX}/${POLL_IDS.networkSubnets}`,
        actionCreator: networksActions.getNetworkSubnetsRequest,
        params: {
          regionId: matchParams.regionId!,
          projectId: matchParams.projectId!,
          networkId: matchParams.networkId!
        }
      })
    );
  });

  useUnmount(() => {
    Object.values(POLL_IDS).forEach((id) => {
      dispatch(
        pollingActions.stopPolling({
          id: `${POLL_ID_PREFIX}/${id}`
        })
      );
    });
    dispatch(enterprisesActions.clear());
    dispatch(projectsActions.clear());
    dispatch(networksActions.clear());
  });

  useEffect(() => {
    if (previousIsNetworkOperationInProgress && !isNetworkOperationInProgress) {
      if (!network) {
        history(getParentPath(location.pathname));
      } else {
        dispatch(
          networksActions.getNetworkRequest({
            regionId: matchParams.regionId!,
            projectId: matchParams.projectId!,
            id: matchParams.networkId!
          })
        );
      }
    }
  }, [
    network,
    history,
    previousIsNetworkOperationInProgress,
    isNetworkOperationInProgress,
    dispatch,
    matchParams.projectId,
    matchParams.networkId,
    matchParams.regionId
  ]);

  useEffect(() => {
    if (previousIsSubnetOperationInProgress && !isSubnetOperationInProgress) {
      dispatch(
        networksActions.getSubnetsRequest({
          regionId: matchParams.regionId!,
          projectId: matchParams.projectId!
        })
      );
    }
  }, [
    previousIsSubnetOperationInProgress,
    isSubnetOperationInProgress,
    dispatch,
    matchParams.projectId,
    matchParams.regionId
  ]);

  useEffect(() => {
    if (previousIsPortsOperationInProgress && !isPortsOperationInProgress) {
      dispatch(
        networksActions.getNetworkInterfacesRequest({
          regionId: matchParams.regionId!,
          projectId: matchParams.projectId!,
          networkId: matchParams.networkId!
        })
      );
    }
  }, [
    dispatch,
    isPortsOperationInProgress,
    matchParams.networkId,
    matchParams.projectId,
    matchParams.regionId,
    previousIsPortsOperationInProgress
  ]);

  useEffect(() => {
    Object.values([POLL_IDS.networkPorts]).forEach((id) => {
      dispatch(
        pollingActions.stopPolling({
          id: `${POLL_ID_PREFIX}/${id}`
        })
      );
    });
    switch (activeTabIndex) {
      case TABS.SUBNETS:
        history({
          search: generateSearchString({
            tab: TAB_TITLES[TABS.SUBNETS]
          })
        });
        break;
      case TABS.PORTS:
        history({
          search: generateSearchString({
            tab: TAB_TITLES[TABS.PORTS]
          })
        });
        dispatch(networksActions.clearNetworkPorts());
        dispatch(
          createPolling({
            id: `${POLL_ID_PREFIX}/${POLL_IDS.networkPorts}`,
            actionCreator: networksActions.getNetworkInterfacesRequest,
            params: {
              regionId: matchParams.regionId!,
              projectId: matchParams.projectId!,
              networkId: matchParams.networkId!
            }
          })
        );
        break;
    }
  }, [
    history,
    activeTabIndex,
    dispatch,
    matchParams.projectId,
    matchParams.regionId,
    matchParams.networkId
  ]);

  const handleConfirmEditNetwork = useCallback(
    (data: { name: string; description: string }) => {
      dispatch(
        networksActions.updateNetworkRequest({
          regionId: matchParams.regionId!,
          projectId: matchParams.projectId!,
          id: matchParams.networkId!,
          data: {
            ...data,
            description: data.name.trim()
          }
        })
      );
      handleCloseDialog();
    },
    [
      dispatch,
      matchParams.networkId,
      matchParams.projectId,
      matchParams.regionId,
      handleCloseDialog
    ]
  );

  const handleConfirmDeleteNetwork = useCallback(() => {
    dispatch(
      networksActions.deleteNetworkRequest({
        regionId: matchParams.regionId!,
        projectId: matchParams.projectId!,
        id: matchParams.networkId!
      })
    );
    handleCloseDialog();
  }, [
    dispatch,
    matchParams.projectId,
    matchParams.networkId,
    matchParams.regionId,
    handleCloseDialog
  ]);

  const handleConfirmCreateSubnet = useCallback(
    (data: {
      allocation_pools: string;
      cidr: string;
      dns_nameservers: string;
      enable_dhcp: boolean;
      disableGateway: boolean;
      gateway_ip: string;
      host_routes: string;
      name: string;
    }) => {
      dispatch(
        networksActions.createSubnetRequest({
          regionId: matchParams.regionId!,
          projectId: matchParams.projectId!,
          data: {
            name: data.name,
            cidr: data.cidr.trim(),
            ...(data.allocation_pools
              ? {
                  allocation_pools: splitMultilineCommaSeparatedList(
                    data.allocation_pools
                  ).map((pool) => {
                    const [start, end] = pool.replace(/\s/g, "").split("-");
                    return {
                      start,
                      end
                    };
                  })
                }
              : {}),
            ...(data.dns_nameservers
              ? {
                  dns_nameservers: splitMultilineCommaSeparatedList(
                    data.dns_nameservers
                  )
                }
              : {}),
            enable_dhcp: data.enable_dhcp,
            ...(data.host_routes
              ? {
                  host_routes: splitMultilineCommaSeparatedList(
                    data.host_routes
                  ).map((route) => {
                    const [destination, nexthop] = route
                      .replace(/\s/g, "")
                      .split("-");
                    return {
                      destination,
                      nexthop
                    };
                  })
                }
              : {}),
            network_id: matchParams.networkId!,
            gateway_ip: data.disableGateway
              ? ""
              : data.gateway_ip.trim() === ""
                ? null
                : data.gateway_ip.trim()
          }
        })
      );
      handleCloseDialog();
    },
    [
      dispatch,
      matchParams.networkId,
      matchParams.projectId,
      matchParams.regionId,
      handleCloseDialog
    ]
  );

  const handleConfirmEditSubnet = useCallback(
    (data: {
      allocation_pools: string;
      dns_nameservers: string;
      enable_dhcp: boolean;
      gateway_ip: string;
      host_routes: string;
      name: string;
      disableGateway: boolean;
    }) => {
      if (selectedItemId) {
        dispatch(
          networksActions.updateSubnetRequest({
            regionId: matchParams.regionId!,
            projectId: matchParams.projectId!,
            id: selectedItemId,
            data: {
              name: data.name,
              allocation_pools: data.allocation_pools
                ? splitMultilineCommaSeparatedList(data.allocation_pools).map(
                    (pool) => {
                      const [start, end] = pool.replace(/\s/g, "").split("-");
                      return {
                        start,
                        end
                      };
                    }
                  )
                : [],
              dns_nameservers: data.dns_nameservers
                ? splitMultilineCommaSeparatedList(data.dns_nameservers)
                : [],
              enable_dhcp: data.enable_dhcp,
              host_routes: data.host_routes
                ? splitMultilineCommaSeparatedList(data.host_routes).map(
                    (route) => {
                      const [destination, nexthop] = route
                        .replace(/\s/g, "")
                        .split("-");
                      return {
                        destination,
                        nexthop
                      };
                    }
                  )
                : [],
              gateway_ip: data.disableGateway
                ? ""
                : data.gateway_ip.trim() === ""
                  ? null
                  : data.gateway_ip.trim()
            }
          })
        );
      }
      handleCloseDialog();
    },
    [
      selectedItemId,
      dispatch,
      matchParams.projectId,
      matchParams.regionId,
      handleCloseDialog
    ]
  );

  const handleConfirmDeleteSubnet = useCallback(() => {
    if (selectedItemId) {
      dispatch(
        networksActions.deleteSubnetRequest({
          regionId: matchParams.regionId!,
          projectId: matchParams.projectId!,
          id: selectedItemId
        })
      );
      handleCloseDialog();
    }
  }, [
    dispatch,
    selectedItemId,
    matchParams.projectId,
    matchParams.regionId,
    handleCloseDialog
  ]);

  const handleConfirmCreatePort = useCallback(
    (data: {
      name: string;
      firewalls?: SelectOption[];
      admin_state_up: boolean;
      port_security: boolean;
      subnet?: SelectOption;
      fixed_ip?: string;
      allowed_address_pairs?: { ip_address: string; mac_address?: string }[];
    }) => {
      const addressPairs = data.allowed_address_pairs?.filter((pair) =>
        pair.ip_address?.trim()
      );
      dispatch(
        networksActions.createNetworkInterfaceRequest({
          regionId: matchParams.regionId!,
          projectId: matchParams.projectId!,
          data: {
            network_id: matchParams.networkId!,
            name: data.name,
            admin_state_up: data.admin_state_up,
            port_security_enabled: data.port_security,
            security_groups: data.firewalls?.map((firewall) => firewall.value),
            fixed_ips: data.subnet?.value
              ? [
                  {
                    subnet_id: data.subnet?.value,
                    ip_address: data.fixed_ip ? data.fixed_ip.trim() : undefined
                  }
                ]
              : undefined,
            allowed_address_pairs: addressPairs?.length
              ? addressPairs.map((pair) => ({
                  ip_address: pair.ip_address.trim(),
                  mac_address: pair.mac_address?.trim() || undefined
                }))
              : undefined
          }
        })
      );
      handleCloseDialog();
    },
    [
      dispatch,
      handleCloseDialog,
      matchParams.networkId,
      matchParams.projectId,
      matchParams.regionId
    ]
  );

  const handleConfirmEditPort = useCallback(
    (data: {
      firewalls?: SelectOption[];
      name?: string;
      port_security?: boolean;
      allowed_address_pairs?: { ip_address: string; mac_address?: string }[];
    }) => {
      if (selectedItemId) {
        const addressPairs = data.allowed_address_pairs?.filter((pair) =>
          pair.ip_address?.trim()
        );
        dispatch(
          networksActions.editNetworkInterfaceRequest({
            regionId: matchParams.regionId!,
            projectId: matchParams.projectId!,
            id: selectedItemId,
            data: {
              name: data.name === undefined ? undefined : data.name,
              port_security_enabled: data.port_security,
              firewalls: data.firewalls
                ? data.firewalls.map((firewall) => firewall.value)
                : [],
              allowed_address_pairs: addressPairs?.length
                ? addressPairs.map((pair) => ({
                    ip_address: pair.ip_address.trim(),
                    mac_address: pair.mac_address?.trim() || undefined
                  }))
                : []
            }
          })
        );
      }
      handleCloseDialog();
    },
    [
      selectedItemId,
      dispatch,
      matchParams.projectId,
      matchParams.regionId,
      handleCloseDialog
    ]
  );

  const handleConfirmEnablePort = useCallback(() => {
    if (selectedItemId) {
      dispatch(
        networksActions.editNetworkInterfaceRequest({
          regionId: matchParams.regionId!,
          projectId: matchParams.projectId!,
          id: selectedItemId,
          data: {
            admin_state_up: true
          }
        })
      );
    }
    handleCloseDialog();
  }, [
    selectedItemId,
    dispatch,
    matchParams.projectId,
    matchParams.regionId,
    handleCloseDialog
  ]);

  const handleConfirmDisablePort = useCallback(() => {
    if (selectedItemId) {
      dispatch(
        networksActions.editNetworkInterfaceRequest({
          regionId: matchParams.regionId!,
          projectId: matchParams.projectId!,
          id: selectedItemId,
          data: {
            admin_state_up: false
          }
        })
      );
    }
    handleCloseDialog();
  }, [
    selectedItemId,
    dispatch,
    matchParams.projectId,
    matchParams.regionId,
    handleCloseDialog
  ]);

  const handleConfirmDeletePort = useCallback(() => {
    if (selectedItemId) {
      dispatch(
        networksActions.deleteNetworkInterfaceRequest({
          regionId: matchParams.regionId!,
          projectId: matchParams.projectId!,
          id: selectedItemId
        })
      );
      handleCloseDialog();
    }
  }, [
    dispatch,
    selectedItemId,
    matchParams.projectId,
    matchParams.regionId,
    handleCloseDialog
  ]);

  const previousSelectedItemId = usePrevious(selectedItemId);
  const deletingItemId = selectedItemId
    ? selectedItemId
    : previousSelectedItemId;
  const deletingSubnetName = subnets?.find(
    (subnet) => subnet.id === deletingItemId
  )?.name;
  const deletingPortDisplayName = networkInterfaces?.find(
    (port) => port.id === deletingItemId
  )?.display_name;

  const dialogProps: {
    [key in DIALOG_TYPES]: Omit<FormDialogProps, "isOpened" | "onCancel">;
  } = {
    [DIALOG_TYPES.EDIT_NETWORK]: {
      onConfirm: handleConfirmEditNetwork,
      title: "Edit network",
      confirmButtonLabel: "Save",
      fields: [
        {
          name: "name",
          type: FIELD_TYPES.TEXT,
          label: "Name",
          defaultValue: network?.name || "",
          rules: string()
            .required()
            .test({
              name: "validateName",
              test: validateName(ENTITY_NAME_LENGTH)
            })
        },
        {
          name: "description",
          type: FIELD_TYPES.TEXT,
          label: "Description",
          defaultValue: network?.description || "",
          rules: string()
        }
      ]
    },
    [DIALOG_TYPES.DELETE_NETWORK]: {
      onConfirm: handleConfirmDeleteNetwork,
      title: `Are you sure you want to delete "${
        network?.name ?? "selected"
      }" network?`,
      confirmButtonLabel: "Delete"
    },
    [DIALOG_TYPES.CREATE_SUBNET]: {
      onConfirm: handleConfirmCreateSubnet,
      title: "Create subnet",
      confirmButtonLabel: "Create",
      fields: [
        {
          name: "name",
          type: FIELD_TYPES.TEXT,
          label: "Name",
          rules: string()
            .required()
            .test({
              name: "validateName",
              test: validateName(ENTITY_NAME_LENGTH)
            })
        },
        {
          name: "enable_dhcp",
          type: FIELD_TYPES.CHECKBOX,
          defaultValue: true,
          label: "Enable DHCP"
        },
        {
          name: "cidr",
          type: FIELD_TYPES.TEXT,
          label: "CIDR",
          rules: string()
            .required()
            .matches(
              REGEX.IP_ADDRESS_WITH_NET_MASK,
              'CIDR address must have the valid format (e.g. "10.0.1.0/24")'
            )
        },
        {
          name: "allocation_pools",
          type: FIELD_TYPES.MULTILINE_TEXT,
          label: "Allocation pools",
          rules: string().test(
            "allocationPoolsValidFormat",
            'Allocation pool must have the valid format (e.g., "10.0.1.10-10.0.1.200") and one entry per line',
            (value) =>
              !value ||
              Boolean(
                splitMultilineCommaSeparatedList(value)
                  .map((record) => record.replace(/\s/g, "").split("-"))
                  .flat()
                  .every((record) => REGEX.IP_ADDRESS.test(record))
              )
          )
        },
        {
          name: "dns_nameservers",
          type: FIELD_TYPES.MULTILINE_TEXT,
          label: "DNS nameservers",
          rules: string().test(
            "DNSNameserversValidFormat",
            'DNS nameserver must have the valid format (e.g., "8.8.8.8") and one entry per line',
            (value) =>
              !value ||
              Boolean(
                splitMultilineCommaSeparatedList(value).every((record) =>
                  REGEX.IP_ADDRESS.test(record)
                )
              )
          )
        },
        {
          name: "gateway_ip",
          type: FIELD_TYPES.TEXT,
          label: "Gateway IP",
          rules: string().matches(REGEX.IP_ADDRESS, {
            message: 'Gateway IP must have the valid format (e.g., "10.0.1.1")',
            excludeEmptyString: true
          })
        },
        {
          name: "disableGateway",
          type: FIELD_TYPES.CHECKBOX,
          label: "Disable gateway"
        },
        {
          name: "host_routes",
          type: FIELD_TYPES.MULTILINE_TEXT,
          label: "Host routes",
          rules: string().test(
            "hostRoutesValidFormat",
            'Host route must have the valid format (e.g., "192.168.200.0/24-10.0.1.254") and one entry per line',
            (value) =>
              !value ||
              Boolean(
                splitMultilineCommaSeparatedList(value)
                  .map((route) => {
                    const [destination, nexthop] = route
                      .replace(/\s/g, "")
                      .split("-");
                    return {
                      destination,
                      nexthop
                    };
                  })
                  .every(
                    (route) =>
                      REGEX.IP_ADDRESS_WITH_NET_MASK.test(route.destination) &&
                      REGEX.IP_ADDRESS.test(route.nexthop)
                  )
              )
          )
        }
      ]
    },
    [DIALOG_TYPES.EDIT_SUBNET]: {
      onConfirm: handleConfirmEditSubnet,
      title: "Edit subnet",
      confirmButtonLabel: "Save",
      fields: [
        {
          name: "name",
          type: FIELD_TYPES.TEXT,
          label: "Name",
          defaultValue:
            subnets?.find((subnet) => subnet.id === selectedItemId)?.name || "",
          rules: string()
            .required()
            .test({
              name: "validateName",
              test: validateName(ENTITY_NAME_LENGTH)
            })
        },
        {
          name: "enable_dhcp",
          type: FIELD_TYPES.CHECKBOX,
          label: "Enable DHCP",
          defaultValue: subnets?.find((subnet) => subnet.id === selectedItemId)
            ?.enable_dhcp
        },
        {
          name: "allocation_pools",
          type: FIELD_TYPES.TEXT,
          label: "Allocation pools",
          defaultValue:
            subnets
              ?.find((subnet) => subnet.id === selectedItemId)
              ?.allocation_pools.map((pool) => `${pool.start}-${pool.end}`)
              .join(",\n") || "",
          rules: string().test(
            "allocationPoolsValidFormat",
            'Allocation pool must have the valid format (e.g., "10.0.1.10-10.0.1.200") and one entry per line',
            (value) =>
              !value ||
              Boolean(
                splitMultilineCommaSeparatedList(value)
                  .map((record) => record.replace(/\s/g, "").split("-"))
                  .flat()
                  .every((record) => REGEX.IP_ADDRESS.test(record))
              )
          )
        },
        {
          name: "dns_nameservers",
          type: FIELD_TYPES.TEXT,
          label: "DNS nameservers",
          defaultValue:
            subnets
              ?.find((subnet) => subnet.id === selectedItemId)
              ?.dns_nameservers.join(",\n") || "",
          rules: string().test(
            "DNSNameserversValidFormat",
            'DNS nameserver must have the valid format (e.g., "8.8.8.8") and one entry per line',
            (value) =>
              !value ||
              Boolean(
                splitMultilineCommaSeparatedList(value).every((record) =>
                  REGEX.IP_ADDRESS.test(record)
                )
              )
          )
        },
        {
          name: "gateway_ip",
          type: FIELD_TYPES.TEXT,
          label: "Gateway IP",
          defaultValue:
            subnets?.find((subnet) => subnet.id === selectedItemId)
              ?.gateway_ip || "",
          rules: string().matches(REGEX.IP_ADDRESS, {
            message: 'Gateway IP must have the valid format (e.g., "10.0.1.1")',
            excludeEmptyString: true
          })
        },
        {
          name: "disableGateway",
          type: FIELD_TYPES.CHECKBOX,
          label: "Disable gateway",
          defaultValue:
            subnets?.find((subnet) => subnet.id === selectedItemId)
              ?.gateway_ip === ""
        },
        {
          name: "host_routes",
          type: FIELD_TYPES.TEXT,
          label: "Host routes",
          defaultValue:
            subnets
              ?.find((subnet) => subnet.id === selectedItemId)
              ?.host_routes.map(
                (route) => `${route.destination}-${route.nexthop}`
              )
              .join(",\n") || "",
          rules: string().test(
            "hostRoutesValidFormat",
            'Host route must have the valid format (e.g., "192.168.200.0/24-10.0.1.254") and one entry per line',
            (value) =>
              !value ||
              Boolean(
                splitMultilineCommaSeparatedList(value)
                  .map((route) => {
                    const [destination, nexthop] = route
                      .replace(/\s/g, "")
                      .split("-");
                    return {
                      destination,
                      nexthop
                    };
                  })
                  .every(
                    (route) =>
                      REGEX.IP_ADDRESS_WITH_NET_MASK.test(route.destination) &&
                      REGEX.IP_ADDRESS.test(route.nexthop)
                  )
              )
          )
        }
      ]
    },
    [DIALOG_TYPES.DELETE_SUBNET]: {
      onConfirm: handleConfirmDeleteSubnet,
      title: `Are you sure you want to delete "${
        deletingSubnetName ?? "selected"
      }" subnet?`,
      confirmButtonLabel: "Delete"
    },
    [DIALOG_TYPES.CREATE_PORT]: {
      onConfirm: handleConfirmCreatePort,
      title: "Create port",
      confirmButtonLabel: "Create",
      fields: [
        {
          name: "name",
          type: FIELD_TYPES.TEXT,
          label: "Name",
          rules: string()
          // .required()
          // .test({
          //   name: "validateName",
          //   test: validateName(ENTITY_NAME_LENGTH)
          // })
        },
        {
          name: "subnet",
          type: FIELD_TYPES.SELECT,
          label: "Subnet (optional)",
          options: subnets?.map((sub) => getSelectOption(sub, "name", "id")),
          rules: selectOptionSchemaNotRequired.notRequired()
          // rules: array(selectOptionSchema).min(1, ERROR_MESSAGES.REQUIRED)
        },
        {
          name: "fixed_ip",
          type: FIELD_TYPES.TEXT,
          label: "Fixed IP (optional)",
          helperText:
            "ℹ️ The IP must be within the range of the selected subnet",
          isHidden: (fieldValues) => !fieldValues?.subnet,
          rules: string().matches(REGEX.IP_ADDRESS, {
            message: 'Fixed IP must have the valid format (e.g., "10.0.1.1")',
            excludeEmptyString: true
          })
        },
        {
          name: "firewalls",
          type: FIELD_TYPES.MULTISELECT,
          helperText: `ℹ️ Firewalls can only be configured when port security is enabled.`,
          defaultValue:
            networkInterfaces && firewalls
              ? firewalls
                  .filter((fw) => {
                    return networkInterfaces
                      .find((i) => i.id === selectedItemId)
                      ?.firewalls_string.includes(fw.name);
                  })
                  .map((firewall) => {
                    return getSelectOption(firewall, "name", "id");
                  })
              : undefined,
          label: "Firewalls (optional)",
          options: firewalls?.map((firewall) =>
            getSelectOption(firewall, "name", "id")
          ),
          isHidden: (fieldValues) => fieldValues.port_security === false,
          rules: array(selectOptionSchema)
        },
        {
          name: "spacing",
          type: FIELD_TYPES.LABEL,
          label: `\n`
        },
        {
          name: "port_security",
          type: FIELD_TYPES.TOGGLE,
          label: "Port security",
          defaultValue: true
        },
        {
          name: "admin_state_up",
          type: FIELD_TYPES.TOGGLE,
          label: "Admin state",
          defaultValue: true
        },
        {
          name: "advanced_settings",
          type: FIELD_TYPES.TOGGLE,
          fullWidth: true,
          isHidden: (fieldValues) => fieldValues.port_security === false,
          label: "Configure allowed address pairs"
        },
        {
          name: "notes",
          type: FIELD_TYPES.NOTES,
          label: `\nℹ️ To configure allowed address pairs or firewalls, enable port security.`,
          isHidden: (fieldValues) => fieldValues.port_security === true
        },

        // {
        //   name: "divider",
        //   type: FIELD_TYPES.DIVIDER,
        //   isHidden: (fieldValues) => !fieldValues.advanced_settings,
        //   label: "Allowed address pairs"
        // },
        {
          name: "allowed_address_pairs",
          type: FIELD_TYPES.FIELD_ARRAY,
          label: `Allowed address pairs`,
          isHidden: (fieldValues) => !fieldValues.advanced_settings,
          rules: array(
            object({
              ip_address: string().matches(REGEX.IP_ADDRESS_OR_CIDR, {
                message:
                  "Must be a valid IP or CIDR (e.g. 10.0.0.1 or 10.0.0.0/24)",
                excludeEmptyString: true
              })
            })
          ),
          subFields: [
            {
              name: "ip_address",
              label: "IP address",
              // placeholder: "192.168.100.100",
              helperText: "IP address or CIDR (e.g. 10.0.0.1 or 10.0.0.0/24)"
            },
            {
              name: "mac_address",
              label: "MAC address",
              placeholder: "fa:16:3e:8e:2c:85",
              helperText: "Optional",
              width: "35%"
            }
          ]
        }
      ]
    },
    [DIALOG_TYPES.EDIT_PORT]: {
      onConfirm: handleConfirmEditPort,
      title: "Edit port",
      isLoading: !networkInterfaces || !firewalls,
      confirmButtonLabel: "Save",
      fields: [
        {
          name: "name",
          type: FIELD_TYPES.TEXT,
          label: "Name",
          defaultValue:
            networkInterfaces?.find((port) => port.id === selectedItemId)
              ?.name || "",
          rules: string()
        },
        {
          name: "firewalls",
          type: FIELD_TYPES.MULTISELECT,
          helperText: `ℹ️ Firewalls can only be configured when port security is enabled.`,
          defaultValue:
            networkInterfaces && firewalls
              ? firewalls
                  .filter((fw) => {
                    return networkInterfaces
                      .find((i) => i.id === selectedItemId)
                      ?.firewalls_string.includes(fw.name);
                  })
                  .map((firewall) => {
                    return getSelectOption(firewall, "name", "id");
                  })
              : undefined,
          label: "Firewalls",
          options: firewalls?.map((firewall) =>
            getSelectOption(firewall, "name", "id")
          ),
          isHidden: (fieldValues) => fieldValues.port_security === false,
          rules: array(selectOptionSchema)
        },
        {
          name: "spacing",
          type: FIELD_TYPES.LABEL,
          label: `\n`
        },
        // {
        //   name: "admin_state_up",
        //   type: FIELD_TYPES.TOGGLE,
        //   label: "Admin state",
        //   fullWidth: true,
        //   defaultValue: networkInterfaces?.find(
        //     (port) => port.id === selectedItemId
        //   )?.admin_state_up
        // },
        {
          name: "port_security",
          type: FIELD_TYPES.TOGGLE,
          label: "Port security",
          defaultValue: networkInterfaces?.find(
            (port) => port.id === selectedItemId
          )?.port_security_enabled
          // isHidden: () => {
          //   const deviceOwner = networkInterfaces?.find(
          //     (port) => port.id === selectedItemId
          //   )?.device_owner;
          //   return deviceOwner !== "" && deviceOwner !== "compute:nova";
          // }
        },
        {
          name: "advanced_settings",
          type: FIELD_TYPES.TOGGLE,
          fullWidth: true,
          isHidden: (fieldValues) => fieldValues.port_security === false,
          label: "Configure allowed address pairs",
          defaultValue:
            (networkInterfaces?.find((port) => port.id === selectedItemId)
              ?.allowed_address_pairs?.length ?? 0) > 0
        },
        {
          name: "notes",
          type: FIELD_TYPES.NOTES,
          label: `\nℹ️ To configure allowed address pairs or firewalls, enable port security.\n`,
          isHidden: (fieldValues) => fieldValues.port_security === true
        },
        {
          name: "allowed_address_pairs",
          type: FIELD_TYPES.FIELD_ARRAY,
          isHidden: (fieldValues) => !fieldValues.advanced_settings,
          label: "Allowed address pairs",
          defaultValue: () => {
            const pairs = networkInterfaces?.find(
              (port) => port.id === selectedItemId
            )?.allowed_address_pairs;
            return pairs?.length
              ? pairs.map(({ ip_address, mac_address }) => ({
                  ip_address: ip_address ?? "",
                  mac_address: mac_address ?? ""
                }))
              : [{}];
          },
          rules: array(
            object({
              ip_address: string().matches(REGEX.IP_ADDRESS_OR_CIDR, {
                message:
                  "Must be a valid IP or CIDR (e.g. 10.0.0.1 or 10.0.0.0/24)",
                excludeEmptyString: true
              })
            })
          ),
          subFields: [
            {
              name: "ip_address",
              label: "IP address",
              helperText: "IP address or CIDR (e.g. 10.0.0.1 or 10.0.0.0/24)"
            },
            {
              name: "mac_address",
              label: "MAC address",
              placeholder: "fa:16:3e:8e:2c:85",
              helperText: "Optional",
              width: "35%"
            }
          ]
        }
        // {
        //   name: "info",
        //   type: FIELD_TYPES.NOTES,
        //   label: `ℹ️ To disable port security, remove all firewalls first.`,
        //   isHidden: (fieldValues) => {
        //     const port = networkInterfaces?.find(
        //       (p) => p.id === selectedItemId
        //     );
        //     const hasFirewalls =
        //       Array.isArray(fieldValues.firewalls) &&
        //       fieldValues.firewalls.length > 0;
        //     return !(
        //       (port?.device_owner === "" ||
        //         port?.device_owner === "compute:nova") &&
        //       !fieldValues.port_security &&
        //       hasFirewalls
        //     );
        //   }
        // }
      ]
    },
    [DIALOG_TYPES.ENABLE_PORT]: {
      onConfirm: handleConfirmEnablePort,
      title: `Are you sure you want to enable "${
        deletingPortDisplayName ?? "selected"
      }" port?`,
      confirmButtonLabel: "Enable"
    },
    [DIALOG_TYPES.DISABLE_PORT]: {
      onConfirm: handleConfirmDisablePort,
      title: `Are you sure you want to disable "${
        deletingPortDisplayName ?? "selected"
      }" port?`,
      confirmButtonLabel: "Disable"
    },
    [DIALOG_TYPES.DELETE_PORT]: {
      onConfirm: handleConfirmDeletePort,
      title: `Are you sure you want to delete "${
        deletingPortDisplayName ?? "selected"
      }" port?`,
      confirmButtonLabel: "Delete"
    }
  };

  const tabContent = [
    <Table<Subnet>
      isSearchEnabled={true}
      isSortingEnabled={true}
      key={"subnetTable"}
      rows={subnets || []}
      columns={subnetTableColumns}
      actions={
        network?.name !== SPECIAL_NAMES.NETWORK ? subnetTableActions : undefined
      }
      isLoading={!subnets}
      toolbarItems={
        network?.name !== SPECIAL_NAMES.NETWORK && (
          <Button onClick={handleCreateSubnetButtonClick} variant={"contained"}>
            Create subnet
          </Button>
        )
      }
    />,
    <Table<TableNetworkInterface>
      isSearchEnabled={true}
      isSortingEnabled={true}
      key={"portTable"}
      rows={networkInterfaces || []}
      columns={portsTableColumns}
      actions={
        network?.name !== SPECIAL_NAMES.NETWORK ? portTableActions : undefined
      }
      isLoading={!networkInterfaces}
      toolbarItems={
        network?.name !== SPECIAL_NAMES.NETWORK && (
          <Button
            onClick={handleCreatePortButtonClick}
            variant={"contained"}
            disabled={!subnets}
          >
            Create port
          </Button>
        )
      }
      itemWithIcon={{
        withText: false,
        column: "port_security_enabled_string",
        getIcon: (row) => {
          const isNovaOrEmpty =
            row.device_owner === "" || row.device_owner === "compute:nova";
          if (
            isNovaOrEmpty &&
            row.port_security_enabled_string === "disabled"
          ) {
            return (
              <s.TypographyColoredDanger>
                {row.port_security_enabled_string}
              </s.TypographyColoredDanger>
            );
          }
          if (isNovaOrEmpty && row.port_security_enabled_string === "enabled") {
            return (
              <s.TypographyColoredSuccess>
                {row.port_security_enabled_string}
              </s.TypographyColoredSuccess>
            );
          }
          return (
            <s.CustomTableCellText>
              {row.port_security_enabled_string}
            </s.CustomTableCellText>
          );
        },
        tooltipText: (row) => {
          if (row.device_owner !== "" && row.device_owner !== "compute:nova") {
            return "Port security uses default settings for this device";
          }
          if (row.port_security_enabled_string === "disabled") {
            return "Port security is disabled for this interface";
          }
          return "";
        }
      }}
    />
  ];

  const title = network?.name;

  return (
    <>
      <Head title={title} />
      {network ? (
        <>
          {organization && project && <Breadcrumbs breadcrumbs={breadcrumbs} />}
          <s.SummaryContainer>
            <s.SummaryColumn>
              <s.Title variant={"h4"} component={"h2"}>
                {title}
              </s.Title>
              <s.SummaryRow>
                <s.DetailsTitle>ID: </s.DetailsTitle>
                <s.DetailsInfoColored>{network.id}</s.DetailsInfoColored>
              </s.SummaryRow>
            </s.SummaryColumn>
            {network?.name !== SPECIAL_NAMES.NETWORK && (
              <s.ActionsContainer>
                <Tooltip title={"Edit"} arrow>
                  <span>
                    <IconButton
                      onClick={handleEditNetworkButtonClick}
                      color={"inherit"}
                      // title={"Edit"}
                    >
                      <EditIcon />
                    </IconButton>
                  </span>
                </Tooltip>
                <Tooltip title={"Delete"} arrow>
                  <span>
                    <IconButton
                      onClick={handleDeleteNetworkButtonClick}
                      color={"inherit"}
                      // title={"Delete"}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </span>
                </Tooltip>
              </s.ActionsContainer>
            )}
          </s.SummaryContainer>
          <Tabs value={activeTabIndex} onChange={handleChangeTab}>
            {Object.values(TAB_TITLES).map((tabTitle) => (
              <Tab key={tabTitle} label={tabTitle} />
            ))}
          </Tabs>
          {tabContent[activeTabIndex]}
        </>
      ) : (
        <Loader text={"Loading data..."} />
      )}
      <FormDialog
        isOpened={dialog.isOpened}
        onCancel={handleCloseDialog}
        fields={dialogProps[dialog.type].fields}
        onConfirm={dialogProps[dialog.type].onConfirm}
        title={dialogProps[dialog.type].title}
        confirmButtonLabel={dialogProps[dialog.type].confirmButtonLabel}
        isLoading={dialogProps[dialog.type].isLoading}
      />
    </>
  );
};
